import { AnswerDto, InputModel } from 'gast-onset-ng2';
import { Component, OnInit, OnDestroy, SimpleChanges, OnChanges, output, input, inject } from "@angular/core";
import { interval, Subscription } from "rxjs";
import { ClozeTestStyleModel } from '../model/cloze-test-style-model';
import { NgClass, NgStyle } from "@angular/common";
import { PlacementTestsService } from '../services/placement-tests.service';
import { ExerciseIndicatorComponent } from "../exercise-indicator/exercise-indicator.component";
import { InputComponent } from "./input/input.component";
import { CustomCheckboxComponent } from "../custom-checkbox/custom-checkbox.component";

@Component({
  selector: "cloze-test-exercise",
  templateUrl: "./exercise.component.html",
  standalone: true,
  styleUrls: ["./exercise.component.scss"],
  imports: [NgClass, NgStyle, ExerciseIndicatorComponent, InputComponent, CustomCheckboxComponent],
})
export class ExerciseComponent implements OnInit, OnDestroy, OnChanges {
  readonly #placementTestsService = inject(PlacementTestsService);
  readonly DONE_LABEL_MOBILE = "FERTIG";
  readonly DONE_LABEL_DESKTOP = "TEXT FERTIG BEARBEITEN";
  readonly NEXT_LABEL_MOBILE = "WEITER";
  readonly NEXT_LABEL_DESKTOP = "WEITER ZUM NÄCHSTEN LÜCKENTEXT";

  readonly nextExercise = output<AnswerDto[]>();
  readonly endTest = output<AnswerDto[]>();

  readonly title = input("");
  readonly content = input("");
  readonly isLastExercise = input(false);
  readonly currentExcerciseNumber = input(0);
  readonly lastExerciseNumber = input<number | null>(null);
  readonly clozeTestStyleModel = input<ClozeTestStyleModel>({} as ClozeTestStyleModel);
  readonly bookingId = input<number | null>(null);
  readonly exerciseId = input<number | null>(null);
  readonly cTestLogo = input("");
  readonly cTestButtonColor = input("");
  readonly mobileMode = input(false);
  readonly totalExercise = input(0);
  readonly serviceUrl = input("");
  readonly lang = input("de");

  timeSubscription!: Subscription;
  timeLimit = 300;
  // timeLimit = 3000;
  timeLeft = this.timeLimit;
  limitWhenToShowTime = 60;
  interval$ = interval(1000);
  lineHeight = '39px';
  readyCheckbox = false;

  inputs: InputModel[] = [];

  doneLabel = "";
  nextLabel = "";
  startTime = Date.now();

  ngOnInit() {
    this.#subscribeToInterval();
    if (this.mobileMode()) {
      this.doneLabel = this.DONE_LABEL_MOBILE;
      this.nextLabel = this.NEXT_LABEL_MOBILE;
      this.lineHeight = '34px';
    } else {
      this.doneLabel = this.DONE_LABEL_DESKTOP;
      this.nextLabel = this.NEXT_LABEL_DESKTOP;
    }
  }

  ngOnDestroy() {
    this.timeSubscription.unsubscribe();
  }

  ngOnChanges(simpleChanges: SimpleChanges) {
    if (simpleChanges["content"]?.currentValue) {
      this.inputs = [];
      this.#splitContent();
      // set inputNumber to first cTest input
      this.#placementTestsService.inputNumber = this.inputs[0].id as number;
      this.#placementTestsService.inputs = this.inputs;
    }
    if (simpleChanges["exerciseId"]?.currentValue) {
      this.#placementTestsService.updateTaskStartTime(
        this.serviceUrl(),
        this.bookingId() as number,
        this.exerciseId() as number
      ).subscribe();
      console.log(`update start time boooking id: ${this.bookingId()} and exercise id : ${this.exerciseId()} `);
    }
  }

  onNextExercise(): void {
    this.timeSubscription?.unsubscribe();
    this.#placementTestsService.updateTaskEndTime(
      this.serviceUrl(),
      this.bookingId() as number,
      this.exerciseId() as number
    ).subscribe();
    console.log(`update end time boooking id: ${this.bookingId} and exercise id : ${this.exerciseId} `);
    this.readyCheckbox = false;
    this.timeLeft = this.timeLimit;
    this.startTime = Date.now();
    this.nextExercise.emit(this.inputs.map((i) => i.answer).filter((a) => a.nr !== null));
    this.#subscribeToInterval();
  }

  onTestEnd(): void {
    this.timeSubscription?.unsubscribe();
    this.#placementTestsService.updateTaskEndTime(
      this.serviceUrl(),
      this.bookingId() as number,
      this.exerciseId() as number
    ).subscribe();
    this.endTest.emit(this.inputs.map((i) => i.answer).filter((a) => a.nr !== null));
  }

  assignCharacter(specialCharacter: string, event: Event): void {
    this.#placementTestsService.setSpecialCharacterToInput(specialCharacter);
    event.preventDefault();
  }

  showTimer() {
    return this.timeLeft <= this.limitWhenToShowTime;
  }

  capitalize(value: string): string {
    if (!value) return value;
    return value.charAt(0).toUpperCase() + value.slice(1).toLowerCase();
  }

  #subscribeToInterval(): void {
    this.timeSubscription = this.interval$.subscribe(() => {
      const delta = Date.now() - this.startTime;
      this.timeLeft = this.timeLimit - Math.floor(delta / 1000);
      if (this.timeLeft <= 0) {
        this.timeSubscription.unsubscribe();
        if (this.isLastExercise()) {
          this.onTestEnd();
        } else {
          this.onNextExercise();
        }
      }
    });
  }

  #splitContent(): void {
    const inputs = this.content().toString().split("</luecke>");
    inputs.forEach((i) => {
      const regex = new RegExp(/<luecke id="luecke_(.*)">/);
      const regex2 = new RegExp(/<luecke id='luecke_(.*)'>/);
      let id = "";
      // Extract id
      const matches = i.match(regex);
      const matches2 = i.match(regex2);
      if (matches) {
        id = matches[1];
      }
      if (matches2) {
        id = matches2[1];
      }

      // Delete all html tags
      i = i.replace(/<\/?[^>]+(>|$)/g, "");

      // Find last word
      let merged: string;
      let lastWord = "";
      if (id) {
        const splitted = i.split(" ");
        lastWord = splitted[splitted.length - 1];
        splitted.pop();
        merged = splitted.join(" ");
      } else {
        merged = i;
      }

      this.inputs.push({
        id: id,
        text: merged,
        lastWord: lastWord,
        answer: {
          buchungId: this.bookingId() as number,
          nr: id === null ? null : parseInt(id, 10),
          aufgabeId: this.exerciseId() as number,
          antwort: "",
        } as AnswerDto,
      } as InputModel);
    });
  }
}
